import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import api from '../../utils/api';
import {
  Box, Typography, Paper, Tabs, Tab, Table, TableBody, TableCell, TableHead, TableRow, Button, TextField, Select, MenuItem,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions, Grid, IconButton, Tooltip, FormControl, InputLabel, Avatar,
  CircularProgress, Snackbar, Alert
} from '@mui/material';
import { Edit, Cancel, Payment as PaymentIcon, Star, DeleteForever, Download, CheckCircle, Visibility, Block, PlayCircleOutline } from '@mui/icons-material';

const DoctorDetails = () => {
  const { id } = useParams();
  const { adminUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [doctor, setDoctor] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [earningsDetails, setEarningsDetails] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [bankEditOpen, setBankEditOpen] = useState(false);
  const [bankForm, setBankForm] = useState({});
  const [reviewsOpen, setReviewsOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [activateOpen, setActivateOpen] = useState(false);
  const [deactivationReason, setDeactivationReason] = useState('');
  const [deletionReason, setDeletionReason] = useState('');
  const [activationReason, setActivationReason] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [newDateTime, setNewDateTime] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [documentUrls, setDocumentUrls] = useState({
    medicalLicense: null,
    degreeCertificate: null,
    idProof: null,
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!adminUser) {
        navigate('/admin');
        return;
      }
      const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
      try {
        setLoading(true);
        const [doctorRes, appointmentsRes, earningsRes, reviewsRes, withdrawalsRes] = await Promise.all([
          api.get(`/admin/users/${id}`, config),
          api.get(`/admin/appointments/doctor/${id}`, config),
          api.get(`/admin/doctor-payment-details/${id}`, config),
          api.get(`/admin/reviews/doctor/${id}`, config),
          api.get(`/admin/withdrawals/doctor/${id}`, config),
        ]);
        const doctorData = {
          ...doctorRes.data,
          earnings: earningsRes.data.summary,
          reviews: reviewsRes.data,
          bankDetails: earningsRes.data.bankDetails,
          profilePicture: doctorRes.data.profilePicture || 'https://via.placeholder.com/150', // Default if no picture
        };
        setDoctor(doctorData);
        setAppointments(appointmentsRes.data || []);
        setEarningsDetails({
          ...earningsRes.data.summary,
          withdrawals: withdrawalsRes.data,
        });
        setEditForm(doctorRes.data);
        setBankForm(earningsRes.data.bankDetails || {});
      } catch (err) {
        console.error('Doctor details fetch error:', err.response?.data);
        if (err.response?.status === 401 || err.response?.status === 403) {
          navigate('/admin');
        }
        setSnackbar({ open: true, message: 'Failed to fetch doctor data', severity: 'error' });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, adminUser, navigate]);

  // Fetch document URLs when switching to Documents tab
  useEffect(() => {
    const fetchDocumentUrls = async () => {
      if (tabValue !== 4 || !adminUser || !id) return;

      const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
      try {
        const [medicalLicenseRes, degreeCertificateRes, idProofRes] = await Promise.all([
          api.get(`/admin/doctors/${id}/documents/medicalLicense`, config),
          api.get(`/admin/doctors/${id}/documents/degreeCertificate`, config),
          api.get(`/admin/doctors/${id}/documents/idProof`, config),
        ]);
        setDocumentUrls({
          medicalLicense: medicalLicenseRes.data.url || null,
          degreeCertificate: degreeCertificateRes.data.url || null,
          idProof: idProofRes.data.url || null,
        });
      } catch (err) {
        console.error('Document URLs fetch error:', err.response?.data);
        setSnackbar({ open: true, message: 'Failed to fetch document URLs', severity: 'error' });
      }
    };
    fetchDocumentUrls();
  }, [tabValue, id, adminUser]);

  const handleTabChange = (event, newValue) => setTabValue(newValue);

  const upcomingAppointments = appointments.filter(app => new Date(app.date) > new Date());
  const pastAppointments = appointments.filter(app => new Date(app.date) <= new Date());
  const filteredAppointments = statusFilter === 'all' ? appointments : appointments.filter(app => app.status === statusFilter);
  const appointmentStats = {
    total: appointments.length,
    completed: appointments.filter(app => app.status === 'completed').length,
    rescheduled: appointments.filter(app => app.status === 'rescheduled').length,
    canceled: appointments.filter(app => app.status === 'canceled').length,
  };

  const handleCancel = (appointmentId) => {
    setSelectedAppointment(appointments.find(app => app._id === appointmentId));
    setCancelDialogOpen(true);
  };

  const confirmCancel = async () => {
    if (!selectedAppointment || !cancelReason) return;
    const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
    setIsProcessing(true);
    try {
      const response = await api.put(`/admin/appointments/${selectedAppointment._id}/cancel`, { reason: cancelReason }, config);
      console.log('Cancel successful:', response.data);

      setAppointments(appointments.map(app =>
        app._id === selectedAppointment._id ? { ...response.data } : app
      ));
      setSnackbar({ open: true, message: 'Appointment canceled successfully', severity: 'success' });
      setCancelDialogOpen(false);
      setCancelReason('');
      setSelectedAppointment(null);
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.response?.data?.details || 'Unknown error occurred';
      console.error('Cancel error:', err.response?.data || err.message);
      setSnackbar({ open: true, message: `Failed to cancel appointment: ${errorMessage}`, severity: 'error' });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReschedule = (appointmentId) => {
    setSelectedAppointment(appointments.find(app => app._id === appointmentId));
    setRescheduleDialogOpen(true);
  };

  const confirmReschedule = async () => {
    if (!selectedAppointment || !newDateTime || !rescheduleReason) return;
    const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
    setIsProcessing(true);
    try {
      const response = await api.put(`/admin/appointments/${selectedAppointment._id}/reschedule`, 
        { newDate: newDateTime, reason: rescheduleReason }, 
        config
      );
      console.log('Reschedule successful:', response.data);

      setAppointments(appointments.map(app =>
        app._id === selectedAppointment._id ? { ...response.data } : app
      ));
      setSnackbar({ open: true, message: 'Appointment rescheduled successfully', severity: 'success' });
      setRescheduleDialogOpen(false);
      setNewDateTime('');
      setRescheduleReason('');
      setSelectedAppointment(null);
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.response?.data?.details || 'Unknown error occurred';
      console.error('Reschedule error:', err.response?.data || err.message);
      setSnackbar({ open: true, message: `Failed to reschedule appointment: ${errorMessage}`, severity: 'error' });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleEditSave = async () => {
    const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
    try {
      const updatedDoctor = await api.put(`/admin/users/${id}`, editForm, config);
      setDoctor({ ...doctor, ...updatedDoctor.data });
      setEditOpen(false);
      setSnackbar({ open: true, message: 'Doctor profile updated successfully', severity: 'success' });
    } catch (err) {
      console.error('Edit error:', err.response?.data);
      setSnackbar({ open: true, message: 'Failed to update doctor profile', severity: 'error' });
    }
  };

  const handleBankSave = async () => {
    const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
    try {
      const updatedDoctor = await api.put(`/admin/users/${id}`, { bankDetails: bankForm }, config);
      setDoctor({ ...doctor, bankDetails: updatedDoctor.data.bankDetails });
      setBankEditOpen(false);
      setSnackbar({ open: true, message: 'Bank details updated successfully', severity: 'success' });
    } catch (err) {
      console.error('Bank update error:', err.response?.data);
      setSnackbar({ open: true, message: 'Failed to update bank details', severity: 'error' });
    }
  };

  const handleDeactivate = async () => {
    setLoading(true);
    const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
    try {
      const response = await api.put(`/admin/doctors/deactivate/${id}`, { reason: deactivationReason }, config);
      setDoctor({ ...doctor, accountStatus: 'deactivated', deactivationReason });
      setDeactivateOpen(false);
      setDeactivationReason('');
      setSnackbar({ open: true, message: response.data.message || 'Doctor account deactivated', severity: 'success' });
    } catch (err) {
      console.error('Deactivate error:', err.response?.data);
      setSnackbar({ open: true, message: 'Failed to deactivate doctor', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async () => {
    setLoading(true);
    const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
    try {
      const response = await api.put(`/admin/doctors/activate/${id}`, { reason: activationReason }, config);
      setDoctor({ ...doctor, accountStatus: 'active', deactivationReason: '' });
      setActivateOpen(false);
      setActivationReason('');
      setSnackbar({ open: true, message: response.data.message || 'Doctor account activated', severity: 'success' });
    } catch (err) {
      console.error('Activate error:', err.response?.data || err.message);
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to activate doctor', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
    try {
      const response = await api.delete(`/admin/doctors/${id}`, { data: { reason: deletionReason }, ...config });
      setDeleteOpen(false);
      setDeletionReason('');
      setSnackbar({ open: true, message: response.data.message || 'Doctor account deleted', severity: 'success' });
      navigate('/admin/doctors');
    } catch (err) {
      console.error('Delete error:', err.response?.data);
      setSnackbar({ open: true, message: 'Failed to delete doctor', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const handleDownloadInvoice = (withdrawalId) => {
    window.open(`https://turemedicine-backend-1.onrender.com/admin/withdrawals/invoice/${withdrawalId}`, '_blank');
  };

  if (!doctor || loading) return <Box display="flex" justifyContent="center"><CircularProgress /></Box>;

  const isActionEnabled = (status) => ['rejected', 'pending', 'rescheduled', 'accepted'].includes(status);

  const getStatusChip = (status) => {
    const statusStyles = {
      pending: { color: 'warning', label: 'Pending' },
      completed: { color: 'success', label: 'Completed' },
      rescheduled: { color: 'info', label: 'Rescheduled' },
      canceled: { color: 'error', label: 'Canceled' },
      rejected: { color: 'default', label: 'Rejected' },
      accepted: { color: 'success', label: 'Accepted' },
    };
    const style = statusStyles[status] || { color: 'default', label: status };
    return (
      <Chip
        label={style.label}
        color={style.color}
        size="small"
        sx={{ fontWeight: 'bold', textTransform: 'uppercase', borderRadius: '4px', minWidth: '90px' }}
      />
    );
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Doctor Details: {doctor.name}</Typography>
      <Paper sx={{ p: 2 }}>
        <Box sx={{ overflowX: { xs: 'auto', md: 'hidden' }, pb: { xs: 1, md: 0 } }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{ mb: 3, minWidth: { xs: 700, md: 'auto' } }}
          >
            <Tab label="Profile" />
            <Tab label="Appointments" />
            <Tab label="Earnings" />
            <Tab label="Bank Details" />
            <Tab label="Documents" />
            <Tab label="Reviews" />
            <Tab label="Actions" />
          </Tabs>
        </Box>

        {tabValue === 0 && (
          <Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Avatar
                    src={`${doctor.profilePicture}?t=${new Date().getTime()}`} // Cache busting
                    alt={doctor.name}
                    sx={{ width: 80, height: 80, mr: 2 }}
                    onError={(e) => {
                      console.log('Profile picture load error:', doctor.profilePicture);
                      e.target.src = 'https://via.placeholder.com/150'; // Fallback image
                    }}
                  />
                  <Typography variant="h6">{doctor.name}</Typography>
                </Box>
                <Typography><strong>ID:</strong> {doctor._id}</Typography>
                <Typography><strong>Email:</strong> {doctor.email}</Typography>
                <Typography><strong>Phone:</strong> {doctor.phone}</Typography>
                <Typography><strong>Address:</strong> {doctor.address}, {doctor.city}, {doctor.state}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography><strong>Specialization:</strong> {doctor.specialization}</Typography>
                <Typography><strong>Experience:</strong> {doctor.experience} years</Typography>
                <Typography><strong>Consultation Fee:</strong> ₹{doctor.consultationFee}</Typography>
                <Typography><strong>Approval Status:</strong> <Chip label={doctor.isVerified ? 'Approved' : 'Pending'} color={doctor.isVerified ? 'success' : 'warning'} /></Typography>
                <Typography><strong>Account Status:</strong> <Chip label={doctor.accountStatus} color={doctor.accountStatus === 'active' ? 'success' : doctor.accountStatus === 'deactivated' ? 'warning' : 'error'} /></Typography>
                {doctor.accountStatus === 'deactivated' && (
                  <Typography><strong>Deactivation Reason:</strong> {doctor.deactivationReason || 'Not specified'}</Typography>
                )}
                <Box mt={2}>
                  <Typography variant="subtitle1"><strong>Availability:</strong></Typography>
                  {Object.entries(doctor.availability || {}).map(([day, times]) => (
                    times.length > 0 && (
                      <Typography key={day}>
                        <strong>{day}:</strong> {times.join(', ')}
                      </Typography>
                    )
                  ))}
                  {Object.values(doctor.availability || {}).every(times => times.length === 0) && (
                    <Typography>No availability set</Typography>
                  )}
                </Box>
                <Button startIcon={<Edit />} onClick={() => setEditOpen(true)} sx={{ mt: 2 }}>Edit Profile</Button>
              </Grid>
            </Grid>
          </Box>
        )}

        {tabValue === 1 && (
          <Box>
            <Typography variant="h6">Appointments</Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <Typography><strong>Total Appointments:</strong> {appointmentStats.total}</Typography>
                <Typography><strong>Completed:</strong> {appointmentStats.completed}</Typography>
                <Typography><strong>Rescheduled:</strong> {appointmentStats.rescheduled}</Typography>
                <Typography><strong>Canceled:</strong> {appointmentStats.canceled}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Filter by Status</InputLabel>
                  <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="rescheduled">Rescheduled</MenuItem>
                    <MenuItem value="canceled">Canceled</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                    <MenuItem value="accepted">Accepted</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Typography variant="subtitle1" mt={2}>Upcoming Appointments</Typography>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Patient</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {upcomingAppointments.map((app) => (
                  <TableRow key={app._id}>
                    <TableCell>{app.patientId?.name || 'N/A'}</TableCell>
                    <TableCell>{new Date(app.date).toLocaleString()}</TableCell>
                    <TableCell>{getStatusChip(app.status)}</TableCell>
                    <TableCell>
                      {isActionEnabled(app.status) && (
                        <>
                          <Button onClick={() => handleCancel(app._id)} color="error" sx={{ mr: 1 }}>Cancel</Button>
                          <Button onClick={() => handleReschedule(app._id)} color="info" sx={{ mr: 1 }}>Reschedule</Button>
                        </>
                      )}
                      <Button startIcon={<Visibility />} onClick={() => navigate(`/admin/appointments/${app._id}`)}>View Details</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Typography variant="subtitle1" mt={2}>Past Appointments</Typography>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Patient</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pastAppointments.map((app) => (
                  <TableRow key={app._id}>
                    <TableCell>{app.patientId?.name || 'N/A'}</TableCell>
                    <TableCell>{new Date(app.date).toLocaleString()}</TableCell>
                    <TableCell>{getStatusChip(app.status)}</TableCell>
                    <TableCell>
                      {isActionEnabled(app.status) && (
                        <>
                          <Button onClick={() => handleCancel(app._id)} color="error" sx={{ mr: 1 }}>Cancel</Button>
                          <Button onClick={() => handleReschedule(app._id)} color="info" sx={{ mr: 1 }}>Reschedule</Button>
                        </>
                      )}
                      <Button startIcon={<Visibility />} onClick={() => navigate(`/admin/appointments/${app._id}`)}>View Details</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Button startIcon={<Visibility />} onClick={() => setHistoryOpen(true)} sx={{ mt: 2 }}>View Appointment History</Button>
          </Box>
        )}

        {tabValue === 2 && (
          <Box>
            <Typography variant="h6" gutterBottom>Earnings & Payouts</Typography>
            {earningsDetails ? (
              <>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={4}>
                    <Typography>
                      <strong>Total Earnings (Before Fees):</strong> ₹{(earningsDetails.totalEarningsBeforeFees || 0).toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography>
                      <strong>Total Amount Available for Withdrawal:</strong> ₹{(earningsDetails.availableBalance || 0).toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography>
                      <strong>Total Withdrawn:</strong> ₹{(earningsDetails.totalWithdrawn || 0).toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography>
                      <strong>Platform Deduction:</strong> ₹{(earningsDetails.platformDeductionFromDoctors || 0).toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography>
                      <strong>Net Earnings:</strong> ₹{(earningsDetails.netDoctorEarnings || 0).toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography>
                      <strong>Completed Appointments:</strong> {earningsDetails.completedAppointments || 0}
                    </Typography>
                  </Grid>
                </Grid>
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6">Pending Withdrawal Requests</Typography>
                  <Table sx={{ mt: 2 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Method</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {earningsDetails.withdrawals && earningsDetails.withdrawals.length > 0 ? (
                        earningsDetails.withdrawals.map((w) => (
                          <TableRow key={w._id}>
                            <TableCell>{new Date(w.requestDate).toLocaleString()}</TableCell>
                            <TableCell>₹{(w.amount || 0).toFixed(2)}</TableCell>
                            <TableCell>
                              <Chip
                                label={w.status}
                                color={w.status === 'approved' ? 'success' : w.status === 'rejected' ? 'error' : 'warning'}
                              />
                              {w.status === 'rejected' && w.rejectionReason && (
                                <Typography variant="caption" sx={{ ml: 1 }}>Reason: {w.rejectionReason}</Typography>
                              )}
                            </TableCell>
                            <TableCell>{w.method || 'N/A'}</TableCell>
                            <TableCell>
                              {w.status === 'approved' && w.invoicePath && (
                                <Button startIcon={<Download />} onClick={() => handleDownloadInvoice(w._id)}>
                                  Invoice
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={5}>No withdrawal requests found.</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </>
            ) : (
              <Typography>Loading earnings details...</Typography>
            )}
          </Box>
        )}

        {tabValue === 3 && (
          <Box>
            <Typography variant="h6">Bank & Payment Details</Typography>
            <Typography><strong>Account Holder:</strong> {doctor.bankDetails?.accountHolderName || 'N/A'}</Typography>
            <Typography><strong>Bank Name:</strong> {doctor.bankDetails?.bankName || 'N/A'}</Typography>
            <Typography><strong>Branch Name:</strong> {doctor.bankDetails?.branchName || 'N/A'}</Typography>
            <Typography><strong>Account Number:</strong> {doctor.bankDetails?.accountNumber || 'N/A'}</Typography>
            <Typography><strong>IFSC Code:</strong> {doctor.bankDetails?.ifscCode || 'N/A'}</Typography>
            <Typography><strong>UPI ID:</strong> {doctor.bankDetails?.upiId || 'N/A'}</Typography>
            <Typography><strong>Last Payment:</strong> {doctor.bankDetails?.lastPaymentDate ? new Date(doctor.bankDetails.lastPaymentDate).toLocaleDateString() : 'N/A'}</Typography>
            <Button startIcon={<Edit />} onClick={() => setBankEditOpen(true)} sx={{ mt: 2 }}>Update Payment Details</Button>
          </Box>
        )}

        {tabValue === 4 && (
          <Box>
            <Typography variant="h6">Documents & Verification</Typography>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Document Type</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Medical License</TableCell>
                  <TableCell>
                    {documentUrls.medicalLicense ? (
                      <Button startIcon={<Download />} onClick={() => window.open(documentUrls.medicalLicense, '_blank')}>
                        Download
                      </Button>
                    ) : (
                      <Typography>No document available</Typography>
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Degree Certificate</TableCell>
                  <TableCell>
                    {documentUrls.degreeCertificate ? (
                      <Button startIcon={<Download />} onClick={() => window.open(documentUrls.degreeCertificate, '_blank')}>
                        Download
                      </Button>
                    ) : (
                      <Typography>No document available</Typography>
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>ID Proof</TableCell>
                  <TableCell>
                    {documentUrls.idProof ? (
                      <Button startIcon={<Download />} onClick={() => window.open(documentUrls.idProof, '_blank')}>
                        Download
                      </Button>
                    ) : (
                      <Typography>No document available</Typography>
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
            {!doctor.isVerified && (
              <Box mt={2}>
                <Button startIcon={<CheckCircle />} onClick={() => {}} color="success">Approve</Button>
                <Button startIcon={<Cancel />} onClick={() => {}} color="error" sx={{ ml: 2 }}>Reject</Button>
              </Box>
            )}
          </Box>
        )}

        {tabValue === 5 && (
          <Box>
            <Typography variant="h6">Ratings & Reviews</Typography>
            <Typography><strong>Average Rating:</strong> {doctor.reviews?.averageRating || 0} / 5 <Star sx={{ color: '#FFD700' }} /></Typography>
            <Button onClick={() => setReviewsOpen(true)} sx={{ mt: 2 }}>View All Reviews</Button>
          </Box>
        )}

        {tabValue === 6 && (
          <Box>
            <Typography variant="h6">Additional Actions</Typography>
            {doctor.accountStatus === 'active' && (
              <Button startIcon={<Block />} onClick={() => setDeactivateOpen(true)} color="warning" sx={{ mr: 2 }}>Deactivate Doctor</Button>
            )}
            {doctor.accountStatus === 'deactivated' && (
              <Button startIcon={<PlayCircleOutline />} onClick={() => setActivateOpen(true)} color="success" sx={{ mr: 2 }}>Activate Doctor</Button>
            )}
            {doctor.accountStatus !== 'deleted' && (
              <Button startIcon={<DeleteForever />} onClick={() => setDeleteOpen(true)} color="error">Delete Doctor</Button>
            )}
          </Box>
        )}

        <Button onClick={() => navigate('/admin/doctors')} sx={{ mt: 3 }}>Back</Button>
      </Paper>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)}>
        <DialogTitle>Edit Doctor Profile</DialogTitle>
        <DialogContent>
          <TextField label="Name" value={editForm.name || ''} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} fullWidth sx={{ mt: 2 }} />
          <TextField label="Email" value={editForm.email || ''} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} fullWidth sx={{ mt: 2 }} />
          <TextField label="Phone" value={editForm.phone || ''} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} fullWidth sx={{ mt: 2 }} />
          <TextField label="Address" value={editForm.address || ''} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} fullWidth sx={{ mt: 2 }} />
          <TextField label="City" value={editForm.city || ''} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} fullWidth sx={{ mt: 2 }} />
          <TextField label="State" value={editForm.state || ''} onChange={(e) => setEditForm({ ...editForm, state: e.target.value })} fullWidth sx={{ mt: 2 }} />
          <TextField label="Specialization" value={editForm.specialization || ''} onChange={(e) => setEditForm({ ...editForm, specialization: e.target.value })} fullWidth sx={{ mt: 2 }} />
          <TextField label="Experience" value={editForm.experience || ''} onChange={(e) => setEditForm({ ...editForm, experience: e.target.value })} fullWidth sx={{ mt: 2 }} />
          <TextField label="Consultation Fee" value={editForm.consultationFee || ''} onChange={(e) => setEditForm({ ...editForm, consultationFee: e.target.value })} fullWidth sx={{ mt: 2 }} />
          <TextField 
            label="Profile Picture URL" 
            value={editForm.profilePicture || ''} 
            onChange={(e) => setEditForm({ ...editForm, profilePicture: e.target.value })} 
            fullWidth 
            sx={{ mt: 2 }} 
            helperText="Enter a valid URL or leave as is to keep the uploaded image"
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Availability Status</InputLabel>
            <Select value={editForm.isActive || false} onChange={(e) => setEditForm({ ...editForm, isActive: e.target.value })}>
              <MenuItem value={true}>Active</MenuItem>
              <MenuItem value={false}>Inactive</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button onClick={handleEditSave} color="primary">Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bankEditOpen} onClose={() => setBankEditOpen(false)}>
        <DialogTitle>Update Bank Details</DialogTitle>
        <DialogContent>
          <TextField label="Holder Name" value={bankForm.accountHolderName || ''} onChange={(e) => setBankForm({ ...bankForm, accountHolderName: e.target.value })} fullWidth sx={{ mt: 2 }} />
          <TextField label="Account Number" value={bankForm.accountNumber || ''} onChange={(e) => setBankForm({ ...bankForm, accountNumber: e.target.value })} fullWidth sx={{ mt: 2 }} />
          <TextField label="IFSC Code" value={bankForm.ifscCode || ''} onChange={(e) => setBankForm({ ...bankForm, ifscCode: e.target.value })} fullWidth sx={{ mt: 2 }} />
          <TextField label="UPI ID" value={bankForm.upiId || ''} onChange={(e) => setBankForm({ ...bankForm, upiId: e.target.value })} fullWidth sx={{ mt: 2 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBankEditOpen(false)}>Cancel</Button>
          <Button onClick={handleBankSave} color="primary">Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deactivateOpen} onClose={() => setDeactivateOpen(false)}>
        <DialogTitle>Deactivate Doctor</DialogTitle>
        <DialogContent>
          <TextField
            label="Deactivation Reason (sent to doctor via email)"
            multiline
            rows={3}
            value={deactivationReason}
            onChange={(e) => setDeactivationReason(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
            disabled={loading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateOpen(false)} disabled={loading}>Cancel</Button>
          <Button onClick={handleDeactivate} color="warning" disabled={!deactivationReason || loading}>
            {loading ? <CircularProgress size={24} /> : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={activateOpen} onClose={() => setActivateOpen(false)}>
        <DialogTitle>Activate Doctor</DialogTitle>
        <DialogContent>
          <TextField
            label="Activation Reason (optional, sent to doctor via email)"
            multiline
            rows={3}
            value={activationReason}
            onChange={(e) => setActivationReason(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
            disabled={loading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivateOpen(false)} disabled={loading}>Cancel</Button>
          <Button onClick={handleActivate} color="success" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Activate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete Doctor</DialogTitle>
        <DialogContent>
          <TextField
            label="Deletion Reason (sent to doctor via email)"
            multiline
            rows={3}
            value={deletionReason}
            onChange={(e) => setDeletionReason(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
            disabled={loading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={loading}>Cancel</Button>
          <Button onClick={handleDelete} color="error" disabled={!deletionReason || loading}>
            {loading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reviewsOpen} onClose={() => setReviewsOpen(false)}>
        <DialogTitle>Patient Reviews</DialogTitle>
        <DialogContent>
          {doctor.reviews?.reviews.map((review, index) => (
            <Box key={index} mb={2}>
              <Typography><strong>Rating:</strong> {review.rating} / 5</Typography>
              <Typography>{review.comment}</Typography>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)}>
        <DialogTitle>Appointment History</DialogTitle>
        <DialogContent>
          <Typography><strong>Total Appointments:</strong> {appointmentStats.total}</Typography>
          <Typography><strong>Completed:</strong> {appointmentStats.completed}</Typography>
          <Typography><strong>Rescheduled:</strong> {appointmentStats.rescheduled}</Typography>
          <Typography><strong>Canceled:</strong> {appointmentStats.canceled}</Typography>
          <Table sx={{ mt: 2 }}>
            <TableHead>
              <TableRow>
                <TableCell>Patient</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAppointments.map((app) => (
                <TableRow key={app._id}>
                  <TableCell>{app.patientId?.name || 'N/A'}</TableCell>
                  <TableCell>{new Date(app.date).toLocaleString()}</TableCell>
                  <TableCell>{getStatusChip(app.status)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Cancel Appointment</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Reason for Cancellation"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            multiline
            rows={3}
            sx={{ mt: 2 }}
            required
            disabled={isProcessing}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)} disabled={isProcessing}>Close</Button>
          <Button onClick={confirmCancel} color="error" disabled={!cancelReason || isProcessing}>
            {isProcessing ? <CircularProgress size={24} /> : 'Confirm Cancel'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rescheduleDialogOpen} onClose={() => setRescheduleDialogOpen(false)}>
        <DialogTitle>Reschedule Appointment</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="New Date & Time"
            type="datetime-local"
            value={newDateTime}
            onChange={(e) => setNewDateTime(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 2 }}
            required
            disabled={isProcessing}
          />
          <TextField
            fullWidth
            label="Reason for Rescheduling"
            value={rescheduleReason}
            onChange={(e) => setRescheduleReason(e.target.value)}
            multiline
            rows={3}
            sx={{ mt: 2 }}
            required
            disabled={isProcessing}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRescheduleDialogOpen(false)} disabled={isProcessing}>Close</Button>
          <Button onClick={confirmReschedule} color="primary" disabled={!newDateTime || !rescheduleReason || isProcessing}>
            {isProcessing ? <CircularProgress size={24} /> : 'Confirm Reschedule'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DoctorDetails;